/**
 * AMD Practitioner Locator
 * Reads a JSON dataset rendered by sections/practitioner-locator.liquid and
 * renders a Google Map (with clustering) plus a searchable, paginated list.
 *
 * Metaobject `for` loops in Liquid are capped at 50 items, so the section
 * paginates by 250 and this script merges the remaining pages via ?page=N
 * fetches before building the map/list.
 */
(function () {
  var MAPS_CALLBACK = '__amdLocatorMapsReady';
  var CLUSTERER_SRC = 'https://unpkg.com/@googlemaps/markerclusterer/dist/index.min.js';
  var EARTH_RADIUS_MILES = 3958.8;

  var mapsPromise = null;
  var clustererPromise = null;

  function loadGoogleMaps(apiKey) {
    if (window.google && window.google.maps) {
      return Promise.resolve();
    }
    if (mapsPromise) {
      return mapsPromise;
    }
    if (!apiKey) {
      return Promise.reject(new Error('Missing Google Maps API key'));
    }

    mapsPromise = new Promise(function (resolve, reject) {
      window[MAPS_CALLBACK] = function () {
        resolve();
      };
      var script = document.createElement('script');
      script.src =
        'https://maps.googleapis.com/maps/api/js?key=' +
        encodeURIComponent(apiKey) +
        '&loading=async&callback=' +
        MAPS_CALLBACK;
      script.async = true;
      script.onerror = function () {
        reject(new Error('Failed to load Google Maps'));
      };
      document.head.appendChild(script);
    });

    return mapsPromise;
  }

  function loadClusterer() {
    if (window.markerClusterer) {
      return Promise.resolve();
    }
    if (clustererPromise) {
      return clustererPromise;
    }

    clustererPromise = new Promise(function (resolve, reject) {
      var script = document.createElement('script');
      script.src = CLUSTERER_SRC;
      script.async = true;
      script.onload = function () {
        resolve();
      };
      script.onerror = function () {
        reject(new Error('Failed to load MarkerClusterer'));
      };
      document.head.appendChild(script);
    });

    return clustererPromise;
  }

  function escapeHtml(value) {
    if (value === null || value === undefined) {
      return '';
    }
    return String(value)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }

  function isApproved(item) {
    if (item.approved === true) {
      return true;
    }
    return String(item.approved).toLowerCase() === 'true';
  }

  function toRadians(degrees) {
    return (degrees * Math.PI) / 180;
  }

  function haversineMiles(a, b) {
    var dLat = toRadians(b.lat - a.lat);
    var dLng = toRadians(b.lng - a.lng);
    var lat1 = toRadians(a.lat);
    var lat2 = toRadians(b.lat);

    var h =
      Math.sin(dLat / 2) * Math.sin(dLat / 2) +
      Math.sin(dLng / 2) * Math.sin(dLng / 2) * Math.cos(lat1) * Math.cos(lat2);

    return 2 * EARTH_RADIUS_MILES * Math.asin(Math.min(1, Math.sqrt(h)));
  }

  function normalizeWebsite(url) {
    if (!url) {
      return '';
    }
    if (/^https?:\/\//i.test(url)) {
      return url;
    }
    return 'https://' + url;
  }

  // Size-graduated cluster colours to match the legacy WP locator: blue for
  // small clusters, yellow for medium, red for large. Individual markers keep
  // Google's default red pin.
  var clusterRenderer = {
    render: function (cluster) {
      var count = cluster.count;
      var fill = count < 10 ? '#2b8fe0' : count < 50 ? '#f2b500' : '#e0503f';
      var size = count < 10 ? 42 : count < 50 ? 50 : 58;
      var svg =
        '<svg xmlns="http://www.w3.org/2000/svg" width="' + size + '" height="' + size + '">' +
        '<circle cx="' + size / 2 + '" cy="' + size / 2 + '" r="' + (size / 2 - 2) + '" fill="' + fill + '" fill-opacity="0.92"/>' +
        '<circle cx="' + size / 2 + '" cy="' + size / 2 + '" r="' + (size / 2) + '" fill="' + fill + '" fill-opacity="0.35"/>' +
        '</svg>';
      return new google.maps.Marker({
        position: cluster.position,
        icon: {
          url: 'data:image/svg+xml;base64,' + window.btoa(svg),
          scaledSize: new google.maps.Size(size, size),
          anchor: new google.maps.Point(size / 2, size / 2),
        },
        label: { text: String(count), color: '#ffffff', fontSize: '13px', fontWeight: '600' },
        zIndex: 1000 + count,
      });
    },
  };

  var PractitionerLocator = function (root) {
    this.root = root;
    this.apiKey = root.dataset.apiKey || '';
    this.clustering = root.dataset.clustering === 'true';
    this.approvedOnly = root.dataset.approvedOnly === 'true';
    this.perPage = parseInt(root.dataset.perPage, 10) || 10;
    this.defaultCenter = {
      lat: parseFloat(root.dataset.defaultLat),
      lng: parseFloat(root.dataset.defaultLng),
    };
    if (isNaN(this.defaultCenter.lat) || isNaN(this.defaultCenter.lng)) {
      this.defaultCenter = { lat: 39.8283, lng: -98.5795 };
    }
    this.defaultZoom = parseInt(root.dataset.defaultZoom, 10) || 4;
    this.defaultRadius = root.dataset.defaultRadius || 'any';
    this.websiteLabel = root.dataset.websiteLabel || 'Website';

    this.mapEl = root.querySelector('[data-locator-map]');
    this.form = root.querySelector('[data-locator-form]');
    this.input = root.querySelector('[data-locator-input]');
    this.radiusSelect = root.querySelector('[data-locator-radius]');
    this.resultsEl = root.querySelector('[data-locator-results]');
    this.paginationEl = root.querySelector('[data-locator-pagination]');
    this.statusEl = root.querySelector('[data-locator-status]');

    this.controller = new AbortController();
    this.raw = [];
    this.totalPages = 1;
    this.currentPage = 1;
    this.config = this.parseConfig();
    this.parseData();

    this.all = [];
    this.filtered = [];
    this.page = 1;
    this.hasSearched = false;
    this.lastCenter = null;

    this.map = null;
    this.markers = [];
    this.clusterer = null;
    this.infoWindow = null;
    this.geocoder = null;
    this.searchMarker = null;
    this.programmaticMove = true;
    this.idleTimer = null;

    this.onSubmit = this.onSubmit.bind(this);
    this.onRadiusChange = this.onRadiusChange.bind(this);
    this.onResultsClick = this.onResultsClick.bind(this);
    this.onPaginationClick = this.onPaginationClick.bind(this);
    this.onMapIdle = this.onMapIdle.bind(this);
  };

  PractitionerLocator.prototype.parseScriptText = function (text) {
    try {
      var data = JSON.parse(text);
      if (!Array.isArray(data)) {
        return [];
      }
      return data
        .map(function (item) {
          return {
            name: item.name || '',
            company: item.company || '',
            address: item.address || '',
            description: item.description || '',
            phone: item.phone || '',
            website: item.website || '',
            image: item.image || '',
            approved: item.approved,
            lat: parseFloat(item.lat),
            lng: parseFloat(item.lng),
          };
        })
        .filter(function (item) {
          return !isNaN(item.lat) && !isNaN(item.lng);
        });
    } catch (error) {
      return [];
    }
  };

  PractitionerLocator.prototype.parseData = function () {
    var node = this.root.querySelector('[data-practitioners]');
    this.raw = node ? this.parseScriptText(node.textContent) : [];

    var meta = this.root.querySelector('[data-locator-meta]');
    if (meta) {
      try {
        var parsed = JSON.parse(meta.textContent);
        this.totalPages = parseInt(parsed.pages, 10) || 1;
        this.currentPage = parseInt(parsed.current, 10) || 1;
      } catch (error) {
        this.totalPages = 1;
        this.currentPage = 1;
      }
    }
  };

  PractitionerLocator.prototype.parseConfig = function () {
    var node = this.root.querySelector('[data-locator-config]');
    if (!node) {
      return { fields: {} };
    }
    try {
      var cfg = JSON.parse(node.textContent);
      cfg.fields = cfg.fields || {};
      return cfg;
    } catch (error) {
      return { fields: {} };
    }
  };

  PractitionerLocator.prototype.applyBase = function () {
    var self = this;
    this.all = this.approvedOnly
      ? this.raw.filter(function (item) {
          return isApproved(item);
        })
      : this.raw.slice();
    this.filtered = this.all.slice();
    this.page = 1;
    return self;
  };

  PractitionerLocator.prototype.init = function () {
    var signal = this.controller.signal;

    if (this.form) {
      this.form.addEventListener('submit', this.onSubmit, { signal: signal });
    }
    if (this.radiusSelect) {
      this.radiusSelect.addEventListener('change', this.onRadiusChange, { signal: signal });
    }
    if (this.resultsEl) {
      this.resultsEl.addEventListener('click', this.onResultsClick, { signal: signal });
    }
    if (this.paginationEl) {
      this.paginationEl.addEventListener('click', this.onPaginationClick, { signal: signal });
    }

    var useStorefront = this.config && this.config.storefrontToken && this.config.shopDomain;

    this.applyBase();
    this.renderResults();
    this.setStatus(
      useStorefront || this.totalPages > 1 ? 'Loading practitioners…' : this.filtered.length + ' practitioners listed.'
    );
    this.initMap();

    if (useStorefront) {
      // Liquid only exposes the first ~250 metaobjects, so pull the full set from
      // the Storefront API (cursor pagination). Falls back to the Liquid data on failure.
      this.loadViaStorefront();
    } else {
      this.loadMorePages();
    }
  };

  PractitionerLocator.prototype.loadViaStorefront = function () {
    var self = this;
    var cfg = this.config;
    var url = 'https://' + cfg.shopDomain + '/api/' + (cfg.apiVersion || '2024-10') + '/graphql.json';
    var query =
      'query($cursor:String){metaobjects(type:' +
      JSON.stringify(cfg.type || 'practitioner') +
      ',first:250,after:$cursor){nodes{fields{key value}} pageInfo{hasNextPage endCursor}}}';

    var all = [];

    var fetchPage = function (cursor, depth) {
      if (depth > 30) {
        return Promise.resolve();
      }
      return fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Shopify-Storefront-Access-Token': cfg.storefrontToken,
        },
        body: JSON.stringify({ query: query, variables: { cursor: cursor || null } }),
        signal: self.controller.signal,
      })
        .then(function (response) {
          return response.ok ? response.json() : null;
        })
        .then(function (json) {
          var mo = json && json.data && json.data.metaobjects;
          if (!mo || !mo.nodes) {
            return;
          }
          mo.nodes.forEach(function (nodeItem) {
            var mapped = self.mapStorefrontFields(nodeItem.fields || []);
            if (mapped) {
              all.push(mapped);
            }
          });
          if (mo.pageInfo && mo.pageInfo.hasNextPage) {
            return fetchPage(mo.pageInfo.endCursor, depth + 1);
          }
        });
    };

    fetchPage(null, 0)
      .then(function () {
        if (!all.length) {
          // Storefront returned nothing — keep the Liquid fallback already rendered.
          self.setStatus(self.filtered.length + ' practitioners listed.');
          return;
        }
        self.raw = all;
        if (!self.hasSearched) {
          self.applyBase();
          self.renderResults();
          self.renderMarkers(self.filtered, false);
          self.setStatus(self.filtered.length + ' practitioners listed.');
        }
      })
      .catch(function () {
        self.setStatus(self.filtered.length + ' practitioners listed.');
      });
  };

  PractitionerLocator.prototype.mapStorefrontFields = function (fields) {
    var keys = this.config.fields || {};
    var f = {};
    fields.forEach(function (field) {
      f[field.key] = field.value;
    });
    var lat = parseFloat(f[keys.latitude || 'latitude']);
    var lng = parseFloat(f[keys.longitude || 'longitude']);
    if (isNaN(lat) || isNaN(lng)) {
      return null;
    }
    return {
      name: f[keys.name || 'name'] || '',
      company: f[keys.company || 'company_name'] || '',
      address: f[keys.address || 'address'] || '',
      description: f[keys.description || 'description'] || '',
      phone: f[keys.phone || 'phone'] || '',
      website: f[keys.website || 'website'] || '',
      image: '',
      approved: f[keys.approved || 'approved'],
      lat: lat,
      lng: lng,
    };
  };

  PractitionerLocator.prototype.destroy = function () {
    this.controller.abort();
    if (this.idleTimer) {
      window.clearTimeout(this.idleTimer);
    }
    if (this.map && window.google && google.maps && google.maps.event) {
      google.maps.event.clearInstanceListeners(this.map);
    }
    if (this.clusterer) {
      this.clusterer.clearMarkers();
    }
    this.markers = [];
    this.map = null;
  };

  PractitionerLocator.prototype.loadMorePages = function () {
    var self = this;
    if (this.totalPages <= 1) {
      return;
    }

    var requests = [];
    for (var page = 1; page <= this.totalPages; page += 1) {
      if (page !== this.currentPage) {
        requests.push(this.fetchPage(page));
      }
    }

    Promise.all(requests)
      .then(function (chunks) {
        chunks.forEach(function (chunk) {
          self.raw = self.raw.concat(chunk);
        });

        if (self.hasSearched) {
          return;
        }

        self.applyBase();
        self.renderResults();
        self.renderMarkers(self.filtered, false);
        self.setStatus(self.filtered.length + ' practitioners listed.');
      })
      .catch(function () {
        /* aborted or failed — keep whatever loaded */
      });
  };

  PractitionerLocator.prototype.fetchPage = function (page) {
    var self = this;
    var url = new URL(window.location.href);
    url.searchParams.set('page', page);

    return fetch(url.toString(), {
      signal: this.controller.signal,
      headers: { 'X-Requested-With': 'XMLHttpRequest' },
    })
      .then(function (response) {
        return response.ok ? response.text() : '';
      })
      .then(function (html) {
        if (!html) {
          return [];
        }
        var doc = new DOMParser().parseFromString(html, 'text/html');
        var node = doc.querySelector('[data-practitioners]');
        return node ? self.parseScriptText(node.textContent) : [];
      })
      .catch(function () {
        return [];
      });
  };

  PractitionerLocator.prototype.initMap = function () {
    var self = this;
    if (!this.mapEl) {
      return;
    }

    loadGoogleMaps(this.apiKey)
      .then(function () {
        self.buildMap();
        if (self.clustering) {
          return loadClusterer().catch(function () {
            self.clustering = false;
          });
        }
        return null;
      })
      .then(function () {
        self.renderMarkers(self.filtered, false);
      })
      .catch(function () {
        self.setStatus('The map could not be loaded. You can still browse the list below.');
      });
  };

  PractitionerLocator.prototype.buildMap = function () {
    this.map = new google.maps.Map(this.mapEl, {
      center: this.defaultCenter,
      zoom: this.defaultZoom,
      mapTypeControl: true,
      streetViewControl: true,
      fullscreenControl: true,
    });
    this.infoWindow = new google.maps.InfoWindow();
    this.geocoder = new google.maps.Geocoder();
    this.map.addListener('idle', this.onMapIdle);
  };

  // When the shopper pans or zooms the map, treat it as an area selection:
  // the list (and markers) update to the practitioners inside the visible area,
  // sorted by proximity to the map centre. Programmatic moves (search, initial
  // render, card clicks) are skipped so they don't clobber their own results.
  PractitionerLocator.prototype.onMapIdle = function () {
    if (this.programmaticMove) {
      this.programmaticMove = false;
      return;
    }
    var self = this;
    if (this.idleTimer) {
      window.clearTimeout(this.idleTimer);
    }
    this.idleTimer = window.setTimeout(function () {
      self.filterToBounds();
    }, 250);
  };

  PractitionerLocator.prototype.filterToBounds = function () {
    if (!this.map || !this.map.getBounds) {
      return;
    }
    var bounds = this.map.getBounds();
    if (!bounds) {
      return;
    }
    var mapCenter = this.map.getCenter();
    var center = { lat: mapCenter.lat(), lng: mapCenter.lng() };

    this.hasSearched = true;
    this.filtered = this.all
      .filter(function (item) {
        return bounds.contains({ lat: item.lat, lng: item.lng });
      })
      .map(function (item) {
        return Object.assign({}, item, {
          distance: haversineMiles(center, { lat: item.lat, lng: item.lng }),
        });
      })
      .sort(function (a, b) {
        return a.distance - b.distance;
      });

    this.page = 1;
    this.renderResults();
    this.renderMarkers(this.filtered, false);
    this.setStatus(this.filtered.length + ' practitioners in this area.');
  };

  PractitionerLocator.prototype.renderMarkers = function (items, fitToItems) {
    var self = this;
    if (!this.map) {
      return;
    }

    this.markers.forEach(function (marker) {
      marker.setMap(null);
    });
    this.markers = [];
    if (this.clusterer) {
      this.clusterer.clearMarkers();
    }

    var positionCounts = {};
    items.forEach(function (item, index) {
      // Spread markers that share the same coordinates so overlapping pins stay
      // individually clickable (otherwise clicking one can open another's data).
      var key = item.lat.toFixed(5) + ',' + item.lng.toFixed(5);
      var seenAt = positionCounts[key] || 0;
      positionCounts[key] = seenAt + 1;
      var position = { lat: item.lat, lng: item.lng };
      if (seenAt > 0) {
        var angle = seenAt * 2.399963; // golden angle for an even spread
        var spread = 0.00014 * (1 + Math.floor(seenAt / 8));
        position = {
          lat: item.lat + spread * Math.cos(angle),
          lng: item.lng + spread * Math.sin(angle),
        };
      }

      var marker = new google.maps.Marker({
        position: position,
        title: item.company || item.name,
      });
      marker.addListener('click', function () {
        self.openInfoWindow(item, marker);
        self.focusCard(index);
      });
      marker._practitionerIndex = index;
      self.markers.push(marker);
    });

    if (this.clustering && window.markerClusterer && window.markerClusterer.MarkerClusterer) {
      if (!this.clusterer) {
        this.clusterer = new window.markerClusterer.MarkerClusterer({
          map: this.map,
          renderer: clusterRenderer,
        });
      }
      this.clusterer.addMarkers(this.markers);
    } else {
      this.markers.forEach(function (marker) {
        marker.setMap(self.map);
      });
    }

    if (fitToItems) {
      this.fitBounds(items);
    }
  };

  PractitionerLocator.prototype.fitBounds = function (items) {
    if (!this.map || !items.length) {
      return;
    }
    var bounds = new google.maps.LatLngBounds();
    items.forEach(function (item) {
      bounds.extend({ lat: item.lat, lng: item.lng });
    });
    if (this.searchMarker) {
      bounds.extend(this.searchMarker.getPosition());
    }
    // Mark as programmatic so the resulting map idle doesn't re-filter by bounds.
    this.programmaticMove = true;
    this.map.fitBounds(bounds);
    if (items.length === 1) {
      this.map.setZoom(Math.min(this.map.getZoom(), 12));
    }
  };

  // Bring a practitioner's card into view (and its page) — used when a map pin
  // is clicked, so the list responds to the map selection.
  PractitionerLocator.prototype.focusCard = function (index) {
    if (!this.resultsEl || index < 0 || index >= this.filtered.length) {
      return;
    }
    var targetPage = Math.floor(index / this.perPage) + 1;
    if (targetPage !== this.page) {
      this.page = targetPage;
      this.renderResults();
    }
    var card = this.resultsEl.querySelector('[data-locator-card="' + index + '"]');
    if (!card) {
      return;
    }
    Array.prototype.forEach.call(this.resultsEl.querySelectorAll('.amd-locator__card.is-active'), function (node) {
      node.classList.remove('is-active');
    });
    card.classList.add('is-active');
    card.scrollIntoView({ behavior: 'smooth', block: 'center' });
  };

  PractitionerLocator.prototype.openInfoWindow = function (item, marker) {
    if (!this.infoWindow || !this.map) {
      return;
    }
    var website = normalizeWebsite(item.website);
    var html =
      '<div class="amd-locator__iw">' +
      (item.company ? '<strong class="amd-locator__iw-company">' + escapeHtml(item.company) + '</strong>' : '') +
      (item.name ? '<span class="amd-locator__iw-name">' + escapeHtml(item.name) + '</span>' : '') +
      (item.address ? '<span class="amd-locator__iw-address">' + escapeHtml(item.address) + '</span>' : '') +
      (item.phone ? '<a class="amd-locator__iw-phone" href="tel:' + escapeHtml(item.phone) + '">' + escapeHtml(item.phone) + '</a>' : '') +
      (website ? '<a class="amd-locator__iw-website" href="' + escapeHtml(website) + '" target="_blank" rel="noopener">' + escapeHtml(this.websiteLabel) + '</a>' : '') +
      '</div>';
    this.infoWindow.setContent(html);
    this.infoWindow.open({ map: this.map, anchor: marker });
  };

  PractitionerLocator.prototype.resolveRadius = function () {
    var raw = this.radiusSelect ? this.radiusSelect.value : '';
    if (raw === '') {
      raw = this.defaultRadius;
    }
    if (!raw || raw === 'any') {
      return Infinity;
    }
    var parsed = parseFloat(raw);
    return isNaN(parsed) ? Infinity : parsed;
  };

  PractitionerLocator.prototype.onSubmit = function (event) {
    event.preventDefault();
    var query = this.input ? this.input.value.trim() : '';

    if (!query) {
      this.hasSearched = false;
      this.lastCenter = null;
      this.applyBase();
      this.renderResults();
      this.renderMarkers(this.filtered, false);
      this.clearSearchMarker();
      this.setStatus(this.filtered.length + ' practitioners listed.');
      return;
    }

    this.hasSearched = true;
    if (this.geocoder) {
      this.geocodeSearch(query);
    } else {
      this.textSearch(query);
    }
  };

  PractitionerLocator.prototype.geocodeSearch = function (query) {
    var self = this;
    this.setStatus('Searching…');

    this.geocoder.geocode(
      { address: query, region: 'us' },
      function (results, status) {
        if (status === 'OK' && results && results[0]) {
          var location = results[0].geometry.location;
          var center = { lat: location.lat(), lng: location.lng() };
          self.applyLocation(center);
        } else {
          self.textSearch(query);
        }
      }
    );
  };

  PractitionerLocator.prototype.applyLocation = function (center) {
    var radius = this.resolveRadius();
    this.lastCenter = center;

    this.filtered = this.all
      .map(function (item) {
        return Object.assign({}, item, {
          distance: haversineMiles(center, { lat: item.lat, lng: item.lng }),
        });
      })
      .filter(function (item) {
        return item.distance <= radius;
      })
      .sort(function (a, b) {
        return a.distance - b.distance;
      });

    this.page = 1;
    this.renderResults();
    this.placeSearchMarker(center);
    this.renderMarkers(this.filtered, true);

    if (!this.filtered.length) {
      this.setStatus('No practitioners found within this radius. Try expanding it.');
    } else {
      this.setStatus(this.filtered.length + ' practitioners found.');
    }
  };

  PractitionerLocator.prototype.textSearch = function (query) {
    var needle = query.toLowerCase();
    this.lastCenter = null;
    this.clearSearchMarker();

    this.filtered = this.all.filter(function (item) {
      return (
        item.name.toLowerCase().indexOf(needle) !== -1 ||
        item.company.toLowerCase().indexOf(needle) !== -1 ||
        item.address.toLowerCase().indexOf(needle) !== -1
      );
    });

    this.page = 1;
    this.renderResults();
    this.renderMarkers(this.filtered, true);
    this.setStatus(this.filtered.length + ' practitioners match “' + query + '”.');
  };

  PractitionerLocator.prototype.onRadiusChange = function () {
    if (this.lastCenter) {
      this.applyLocation(this.lastCenter);
    }
  };

  PractitionerLocator.prototype.placeSearchMarker = function (center) {
    if (!this.map) {
      return;
    }
    this.clearSearchMarker();
    this.searchMarker = new google.maps.Marker({
      position: center,
      map: this.map,
      icon: {
        path: google.maps.SymbolPath.CIRCLE,
        scale: 8,
        fillColor: '#1e4459',
        fillOpacity: 1,
        strokeColor: '#ffffff',
        strokeWeight: 2,
      },
      title: 'Search location',
    });
  };

  PractitionerLocator.prototype.clearSearchMarker = function () {
    if (this.searchMarker) {
      this.searchMarker.setMap(null);
      this.searchMarker = null;
    }
  };

  PractitionerLocator.prototype.renderResults = function () {
    if (!this.resultsEl) {
      return;
    }

    var totalPages = Math.max(1, Math.ceil(this.filtered.length / this.perPage));
    if (this.page > totalPages) {
      this.page = totalPages;
    }

    var start = (this.page - 1) * this.perPage;
    var pageItems = this.filtered.slice(start, start + this.perPage);

    if (!pageItems.length) {
      this.resultsEl.innerHTML = '<p class="amd-locator__empty">No practitioners to display.</p>';
      this.renderPagination(totalPages);
      return;
    }

    var self = this;
    this.resultsEl.innerHTML = pageItems
      .map(function (item, index) {
        return self.buildCard(item, start + index);
      })
      .join('');

    this.renderPagination(totalPages);
  };

  PractitionerLocator.prototype.buildCard = function (item, index) {
    var website = normalizeWebsite(item.website);
    var media = item.image
      ? '<div class="amd-locator__card-media"><img src="' + escapeHtml(item.image) + '" alt="' + escapeHtml(item.company || item.name) + '" loading="lazy" width="72" height="72"></div>'
      : '';

    var phone = item.phone
      ? '<a class="amd-locator__card-phone" href="tel:' + escapeHtml(item.phone) + '">' + escapeHtml(item.phone) + '</a>'
      : '';

    var company = item.company
      ? '<h2 class="amd-locator__card-company">' + escapeHtml(item.company) + '</h2>'
      : '';

    var name = item.name
      ? '<p class="amd-locator__card-name">' + escapeHtml(item.name) + '</p>'
      : '';

    var description = item.description
      ? '<p class="amd-locator__card-desc">' + escapeHtml(item.description) + '</p>'
      : '';

    var distance =
      typeof item.distance === 'number'
        ? '<span class="amd-locator__card-distance">' + item.distance.toFixed(1) + ' mi</span>'
        : '';

    var websiteButton = website
      ? '<a class="btn-primary amd-locator__card-website" href="' + escapeHtml(website) + '" target="_blank" rel="noopener">' + escapeHtml(this.websiteLabel) + '</a>'
      : '';

    return (
      '<article class="amd-locator__card" data-locator-card="' + index + '">' +
      media +
      '<div class="amd-locator__card-body">' +
      '<div class="amd-locator__card-head">' +
      '<div class="amd-locator__card-heading">' + company + name + '</div>' +
      phone +
      '</div>' +
      description +
      '<div class="amd-locator__card-foot">' + distance + websiteButton + '</div>' +
      '</div>' +
      '</article>'
    );
  };

  PractitionerLocator.prototype.renderPagination = function (totalPages) {
    if (!this.paginationEl) {
      return;
    }
    if (totalPages <= 1) {
      this.paginationEl.innerHTML = '';
      return;
    }

    var current = this.page;
    var pages = [];
    var windowSize = 2;

    for (var i = 1; i <= totalPages; i += 1) {
      if (i === 1 || i === totalPages || (i >= current - windowSize && i <= current + windowSize)) {
        pages.push(i);
      } else if (pages[pages.length - 1] !== '…') {
        pages.push('…');
      }
    }

    var html = '';
    if (current > 1) {
      html += '<button type="button" class="amd-locator__page" data-page="' + (current - 1) + '">Prev</button>';
    }
    pages.forEach(function (value) {
      if (value === '…') {
        html += '<span class="amd-locator__page-ellipsis">…</span>';
      } else {
        html +=
          '<button type="button" class="amd-locator__page' +
          (value === current ? ' is-active' : '') +
          '" data-page="' + value + '"' +
          (value === current ? ' aria-current="page"' : '') +
          '>' + value + '</button>';
      }
    });
    if (current < totalPages) {
      html += '<button type="button" class="amd-locator__page" data-page="' + (current + 1) + '">Next</button>';
    }

    this.paginationEl.innerHTML = html;
  };

  PractitionerLocator.prototype.onPaginationClick = function (event) {
    var button = event.target.closest('[data-page]');
    if (!button) {
      return;
    }
    var page = parseInt(button.dataset.page, 10);
    if (isNaN(page) || page === this.page) {
      return;
    }
    this.page = page;
    this.renderResults();
    if (this.resultsEl) {
      this.resultsEl.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  PractitionerLocator.prototype.onResultsClick = function (event) {
    if (event.target.closest('a')) {
      return;
    }
    var card = event.target.closest('[data-locator-card]');
    if (!card) {
      return;
    }
    var index = parseInt(card.dataset.locatorCard, 10);
    var item = this.filtered[index];
    if (!item || !this.map) {
      return;
    }

    this.programmaticMove = true;
    this.map.panTo({ lat: item.lat, lng: item.lng });
    this.map.setZoom(Math.max(this.map.getZoom(), 11));

    Array.prototype.forEach.call(this.resultsEl.querySelectorAll('.amd-locator__card.is-active'), function (node) {
      node.classList.remove('is-active');
    });
    card.classList.add('is-active');

    var marker = this.markers[index];
    if (marker) {
      this.openInfoWindow(item, marker);
    }
    if (this.mapEl) {
      this.mapEl.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  };

  PractitionerLocator.prototype.setStatus = function (message) {
    if (this.statusEl) {
      this.statusEl.textContent = message;
    }
  };

  var instances = new Map();

  function initLocators(scope) {
    var root = scope instanceof HTMLElement || scope instanceof Document ? scope : document;
    var nodes = root.querySelectorAll('[data-practitioner-locator]');

    nodes.forEach(function (node) {
      if (instances.has(node)) {
        return;
      }
      var instance = new PractitionerLocator(node);
      instance.init();
      instances.set(node, instance);
    });
  }

  function destroyLocators(scope) {
    if (!(scope instanceof HTMLElement)) {
      return;
    }
    var nodes = scope.querySelectorAll('[data-practitioner-locator]');
    nodes.forEach(function (node) {
      var instance = instances.get(node);
      if (instance) {
        instance.destroy();
        instances.delete(node);
      }
    });
    if (scope.matches('[data-practitioner-locator]')) {
      var self = instances.get(scope);
      if (self) {
        self.destroy();
        instances.delete(scope);
      }
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      initLocators(document);
    });
  } else {
    initLocators(document);
  }

  document.addEventListener('shopify:section:load', function (event) {
    initLocators(event.target);
  });

  document.addEventListener('shopify:section:unload', function (event) {
    destroyLocators(event.target);
  });
})();
